import { z } from 'zod';

export const CALENDAR_ADD_GUESTS_REQUIRED_MESSAGE =
  'Select when guests should be added to the calendar';

export const CalendarEventFormSchema = z.object({
  calendarNotifications: z.boolean().default(true),
  calendarAddGuestsSetting: z.enum(['invited', 'rsvped']).optional(),
  calendarEventVisibility: z.enum(['busy', 'free']).default('busy')
});

export type CalendarEventFormValues = z.infer<typeof CalendarEventFormSchema>;

/**
 * Applied with `.and()` rather than `.superRefine()` on the host schema. A chained
 * refinement never runs once the host object aborts, and a missing required field is
 * enough to abort it — so on a half-filled create form the resolver would report no
 * error here at all, leaving the field with nothing to render. An intersection parses
 * independently, so this issue surfaces alongside the other required-field errors.
 *
 * Picks the two fields it reads because a zod object strips keys it doesn't declare,
 * which would leave the refinement inspecting an empty object.
 *
 * Only required when calendar sync is on — the picker is hidden otherwise, so an
 * unconditional rule would block submit on a field that isn't on screen.
 */
export const CalendarGuestsRequirementSchema = CalendarEventFormSchema.pick({
  calendarNotifications: true,
  calendarAddGuestsSetting: true
}).superRefine((data, ctx) => {
  if (data.calendarNotifications && !data.calendarAddGuestsSetting) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: CALENDAR_ADD_GUESTS_REQUIRED_MESSAGE,
      path: ['calendarAddGuestsSetting']
    });
  }
});

export const CalendarEventFormValidationSchema = CalendarEventFormSchema.and(
  CalendarGuestsRequirementSchema
);

/**
 * Projects the form's tri-state selection onto the backend's two booleans, which have
 * no "unset" representation. With sync off the picker is hidden, so there is no
 * selection to honour and `sendCalendarInvites` must not inherit a stale one.
 */
export const toCalendarWireValues = (
  values: Partial<
    Pick<CalendarEventFormValues, 'calendarNotifications' | 'calendarAddGuestsSetting'>
  >
): { calendarNotifications: boolean; sendCalendarInvites: boolean } => {
  const calendarNotifications = values.calendarNotifications ?? true;

  return {
    calendarNotifications,
    sendCalendarInvites: calendarNotifications && values.calendarAddGuestsSetting === 'invited'
  };
};
